import {stringToSlug} from 'airship/utils/string-to-slug'
import {WebSdkWrapper} from 'airship/utils/web-sdk'

import * as debugLog from '../debug-log'
import {
  Action,
  AnalyticsProvider,
  Context,
  EventProps,
  SimpleAnalyticsProvider,
  UserBackendProps,
} from './types'
import {joinEventName} from './util'

export const AMPLITUDE_LABEL = 'Amplitude'

/**
 * GenericClient implements the typical `client.track(name, data)` API. It
 * works as-is with Kissmetrics, Mixpanel, Heap, etc.
 */
export class GenericClient implements AnalyticsProvider {
  public supportsLegacyEvents = true

  public constructor(
    public readonly label: string,
    public readonly client?: SimpleAnalyticsProvider
  ) {}

  public async track(
    context: Context,
    action: Action,
    description: string,
    data?: EventProps
  ): Promise<boolean> {
    if (!this.client) {
      return false
    }

    const eventName = joinEventName(context, action, description)
    this.client.track(eventName, data)
    return true
  }

  public async trackLegacyEvent(
    eventName: string,
    data?: EventProps | undefined
  ): Promise<boolean> {
    if (!this.client) {
      return false
    }

    this.client.track(eventName, data)
    return true
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public async setUserId(): Promise<boolean> {
    // `track` looks the same across these "generic" platforms, but the
    // identification calls do not. We'll have to start making more
    // platform-specific clients. For now, Amplitude is our chosen platform, so
    // we'll only bother doing it there. We can add more clients later if there
    // are other platforms in the mix.
    return false
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public async setUserProps(): Promise<boolean> {
    // See comment for `setUserId`
    return false
  }

  public async trackPageView(): Promise<boolean> {
    return false
  }
}

export class AirshipSdkClient implements AnalyticsProvider {
  public label = 'Airship'
  public supportsLegacyEvents = false

  private static BANNED_PROPERTIES: (keyof UserBackendProps)[] = [
    'email',
    'username',
  ]

  private _sdk: Promise<WebSdkWrapper>
  private _context?: Partial<UserBackendProps>

  constructor(sdk: Promise<WebSdkWrapper>) {
    this._sdk = sdk

    // it's possible for the web sdk to not be present, but we don't want it to
    // throw; swallow this error
    sdk.catch((e) => debugLog.log('airship analytics are not available: %s', e))
  }

  async track(
    context: Context,
    action: Action,
    description: string,
    data?: EventProps | undefined
  ): Promise<boolean> {
    try {
      const sdk = await this._sdk
      await sdk._track(`web_${stringToSlug(action)}`, {
        context: stringToSlug(context),
        description: description.toLocaleLowerCase(),
        ...data,
        ...this._context,
      })
      return true
    } catch (e) {
      debugLog.log(
        'failed to submit airship analytics event "%s" with error: %s',
        context,
        e
      )
      return false
    }
  }

  async trackLegacyEvent(): Promise<boolean> {
    throw new Error('not implemented')
  }

  async setUserProps(props: UserBackendProps): Promise<boolean> {
    // the web sdk already identifies itself when included in airship by its own
    // means; this is just for setting sdk event properties that will be used
    // as event context
    this._context = Object.entries(props)
      .filter(
        ([key]) =>
          !AirshipSdkClient.BANNED_PROPERTIES.includes(
            key as keyof UserBackendProps
          )
      )
      .reduce((prev, [key, value]) => {
        prev[key as keyof UserBackendProps] = value
        return prev
      }, {} as Partial<UserBackendProps>)
    return true
  }

  public async trackPageView(screenName: string): Promise<boolean> {
    const sdk = await this._sdk
    await sdk.trackScreen(screenName)
    return true
  }
}
